import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SearchablePicker } from '@/components/ui/SearchablePicker';
import { useAgents } from '@/hooks/data/useAgents';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the chosen target profile id when the user confirms. */
  onSelect: (profileId: string) => void;
  /** Number of clients being delegated (for the bulk case). */
  count?: number;
  busy?: boolean;
}

// Shared "assign to another user" picker for the Kundenpool. Lists active
// makler + call-center users (the people who work clients). Used for both
// per-row and bulk delegation.
export function PoolDelegateDialog({ open, onOpenChange, onSelect, count, busy }: Props) {
  const { t } = useTranslation();
  const { data: agents = [] } = useAgents();
  const [target, setTarget] = useState('');

  const options = agents
    .filter((a) => a.status === 'active' && (a.role === 'makler' || a.role === 'call_center'))
    .map((a) => ({
      value: a.id,
      label: a.full_name,
      secondary: `${a.agent_id} · ${t(`agents.role.${a.role}`, a.role)}`,
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('clients.pool.delegate_title', 'An Benutzer zuweisen')}
            {count && count > 1 ? ` (${count})` : ''}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-sm text-ink-3">
            {t(
              'clients.pool.delegate_hint',
              'Klient(en) werden diesem Benutzer exklusiv zugewiesen (30-Tage-Sperre).',
            )}
          </p>
          <SearchablePicker
            value={target}
            onChange={setTarget}
            placeholder={t('clients.pool.delegate_pick', 'Makler oder Call-Center suchen …')}
            emptyText={t('common.no_matches', 'Keine Treffer')}
            options={options}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Abbrechen')}
          </Button>
          <Button
            type="button"
            disabled={!target || busy}
            onClick={() => target && onSelect(target)}
          >
            {busy
              ? t('clients.pool.assigning', 'Wird zugewiesen …')
              : t('clients.pool.assign', 'Zuweisen')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
